import { latLng } from "leaflet";
import * as _ from "lodash";
import { call, put, select } from "redux-saga/effects";
import { v4 as uuidv4 } from "uuid";

import actions from "@/actions";
import { Map } from "@/utils";

const snappingToGrid = (point, mapSettings) => {
    const { tileSizeX, tileSizeY, gridOffSetX, gridOffSetY } =
        mapSettings.ceilingDetails;
    const gridPnt = Map.getNearestGridPoint(
        point,
        tileSizeX,
        tileSizeY,
        gridOffSetX,
        gridOffSetY,
    );
    const smallestDeltaVector = Map.subtractLatLng(gridPnt, point);
    point.lat += smallestDeltaVector.lat;
    point.lng += smallestDeltaVector.lng;
    return point;
};

function getTopLeftPointOfClipboard(clipboard) {
    const lat = _.reduce(clipboard, (result, light) =>
        light.bounds.getNorth() >= result.bounds.getNorth() ? light : result,
    ).bounds.getNorth();

    const lng = _.reduce(clipboard, (result, light) =>
        light.bounds.getWest() <= result.bounds.getWest() ? light : result,
    ).bounds.getWest();
    return latLng({ lat, lng });
}

/**
 * Pastes clipboard lights at the tapped map position, offsetting from the original top-left corner.
 *
 * @param root0 - The action.
 * @param root0.payload - The map position to paste at.
 * @yields Selects clipboard data and dispatches pasted light positions.
 */
function* paste({ payload }) {
    try {
        const { clipboard, snapping, mapSettings, lights } = yield select(
            ({ map, topBar }) => ({
                clipboard: map.general.clipboard,
                snapping: topBar.options.snapping,
                mapSettings: map.general.settings,
                lights: map.values.mode.present.lights,
            }),
        );
        const clipboardAreaCorner = yield call(
            getTopLeftPointOfClipboard,
            clipboard,
        );
        let point = payload;
        if (snapping) {
            point = snappingToGrid(point, mapSettings);
        }
        const modifier = {
            lat: point.lat - clipboardAreaCorner.lat,
            lng: point.lng - clipboardAreaCorner.lng,
        };
        let anyOverlapped = false;
        const copiedLights = [];
        _.each(clipboard, (light) => {
            const copiedLight = _.cloneDeep(light);
            const newPoint = {
                lat: copiedLight.point.lat + modifier.lat,
                lng: copiedLight.point.lng + modifier.lng,
            };
            copiedLight.bleId = "0";
            copiedLight.ciCode = 0;
            copiedLight.bleUUID = null;
            copiedLight.floorSpaceCatalog_ID = null;
            copiedLight.locationDescription = "";
            copiedLight.selectedFlags = [];
            copiedLight.id = 0;
            copiedLight.positionId = 0;
            copiedLight.lastPoint = newPoint;
            copiedLight.point = newPoint;
            copiedLight._id = uuidv4();
            const isOverlapping = copiedLight.isOverlapping(lights);
            if (isOverlapping) {
                anyOverlapped = true;
                return;
            }
            copiedLights.push(copiedLight);
        });
        if (anyOverlapped) {
            yield put(
                actions.window.showMessage(
                    "We couldn't place all of the lights in your array. The skipped lights were either out of bounds of the map dimensions, or overlapped existing lights.",
                    "warning",
                ),
            );
        } else {
            yield put(actions.window.showMessage("Lights pasted!", "success"));
        }
        yield put(actions.map.values.mode.updateLights(copiedLights));
        yield put(actions.map.general.selectLights(copiedLights, "multiple"));
        yield put(
            actions.window.setInformation(
                "Drag and drop selected lights around the map, use toolbar actions, or bulk edit properties.",
            ),
        );
        yield put(actions.map.general.setPasteMode(false, []));
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log("err on paste lights:", err);
    }
}

export default paste;
